import { ExamInterface, ExerciseInterface, ObjectiveInterface } from "../../../interfaces/examInterfaces";

export const MAIN_TYPES = {
  SHOW_SOLUTIONS: "showSolutions",
  EXAM_DATA: "examData",
  INITIAL_DATA: "initalData",
  SELECTED_OBJECTIVES: "selectedObjectives",
  SELECTED_EXERCISES: "selectedExercises",
  RESET_STATE: "resetState",
  EXAM_LOADING: "examLoading",
  IMAGE_URLS: "imageUrls",
  SOLUTION_IMAGE_URLS: "solutionImageUrls",
  SELECTED_OBJECTIVE_ID: "selectedObjectiveId",
  PAGES: "pages",
  TOTAL_POINTS: "totalPoints",
  ACTIVE_TAB: "activeTab",
  EXERCISES_COPY: "exercisesCopy",
  PAGES_CREATED: "pagesCreated",
  IS_MEASURING_CONTAINER: "isMeasuringContainer",
  IS_MEASURING_EXERCISES: "isMeasuringExercises",
  EXERCISE_HEIGHTS: "exerciseHeights",
  FIRST_PAGE_LOAD: "firstPageLoad",
  OPEN_SNACKBAR_SAVE: "openSnackbarSave",
  OPEN_SNACKBAR_SAVED: "openSnackbarSaved",
  NAME_CHANGED: "nameChanged",
  HAS_UNSAVED_CHANGES: "hasUnsavedChanges",
  ADD_OBJECTIVE: "addObjective",
  REMOVE_OBJECTIVE: "removeObjective",
  OVERWRITE_OBJECTIVES: "overwriteObjectives",
  ZOOM_FACTOR: "zoomFactor",
  SCROLL_HEIGHT: "scrollHeight",
};

export const MAIN_INITIAL_STATE: MainState = {
  showSolutions: false,
  examData: { id: "", grade: 6, name: "", objectives: {} },
  initialData: { id: "", grade: 6, name: "", objectives: {} },
  selectedObjectives: [],
  selectedExercises: [],
  examLoading: true,
  imageUrls: {},
  solutionImageUrls: {},
  selectedObjectiveId: null,
  pages: [],
  totalPoints: 0,
  activeTab: 0,
  exercisesCopy: [],
  pagesCreated: false,
  isMeasuringContainer: false,
  isMeasuringExercises: false,
  exerciseHeights: {},
  firstPageLoad: true,
  openSnackbarSave: false,
  openSnackbarSaved: false,
  nameChanged: false,
  hasUnsavedChanges: false,
  objectivesCopy: {},
  zoomFactor: 0.3,
  scrollHeight: 0,
};

export interface MainState {
  showSolutions: boolean;
  examData: ExamInterface;
  initialData: ExamInterface;
  selectedObjectives: number[];
  selectedExercises: number[];
  examLoading: boolean;
  imageUrls: Record<string, string>;
  solutionImageUrls: Record<string, string>;
  selectedObjectiveId: string | null;
  pages: ExerciseInterface[][];
  totalPoints: number;
  activeTab: number;
  exercisesCopy: ExerciseInterface[];
  pagesCreated: boolean;
  isMeasuringContainer: boolean;
  isMeasuringExercises: boolean;
  exerciseHeights: Record<string, number>;
  firstPageLoad: boolean;
  openSnackbarSave: boolean;
  openSnackbarSaved: boolean;
  nameChanged: boolean;
  hasUnsavedChanges: boolean;
  objectivesCopy: { [key: string]: ObjectiveInterface };
  zoomFactor: number;
  scrollHeight: number;
}

export interface Action {
  type: string;
  payload: any;
}

export const mainReducer = (state = MAIN_INITIAL_STATE, action: Action): MainState => {
  switch (action.type) {
    case MAIN_TYPES.SHOW_SOLUTIONS:
      return { ...state, showSolutions: action.payload };

    case MAIN_TYPES.EXAM_DATA:
      return { ...state, examData: action.payload };

    case MAIN_TYPES.INITIAL_DATA:
      return { ...state, initialData: action.payload };

    case MAIN_TYPES.SELECTED_OBJECTIVES:
      return {
        ...state,
        selectedObjectives: [
          ...state.selectedObjectives,
          ...action.payload.filter((id: number) => !state.selectedObjectives.includes(id)),
        ],
      };

    case MAIN_TYPES.SELECTED_EXERCISES:
      return {
        ...state,
        selectedExercises: [
          ...state.selectedExercises,
          ...action.payload.filter((id: number) => !state.selectedExercises.includes(id)),
        ],
      };

    case MAIN_TYPES.EXAM_LOADING:
      return { ...state, examLoading: action.payload };

    case MAIN_TYPES.IMAGE_URLS:
      return {
        ...state,
        imageUrls: { ...state.imageUrls, [action.payload.id]: action.payload.url },
      };

    case MAIN_TYPES.SOLUTION_IMAGE_URLS:
      return {
        ...state,
        solutionImageUrls: { ...state.solutionImageUrls, [action.payload.id]: action.payload.url },
      };

    case MAIN_TYPES.SELECTED_OBJECTIVE_ID:
      return {
        ...state,
        selectedObjectiveId: action.payload,
      };

    case MAIN_TYPES.PAGES:
      return {
        ...state,
        pages: action.payload,
      };

    case MAIN_TYPES.TOTAL_POINTS:
      return {
        ...state,
        totalPoints: action.payload,
      };

    case MAIN_TYPES.ACTIVE_TAB:
      return {
        ...state,
        activeTab: action.payload,
      };

    case MAIN_TYPES.EXERCISES_COPY:
      return {
        ...state,
        exercisesCopy: action.payload,
      };

    case MAIN_TYPES.PAGES_CREATED:
      return {
        ...state,
        pagesCreated: action.payload,
      };

    case MAIN_TYPES.IS_MEASURING_CONTAINER:
      return {
        ...state,
        isMeasuringContainer: action.payload,
      };

    case MAIN_TYPES.IS_MEASURING_EXERCISES:
      return {
        ...state,
        isMeasuringExercises: action.payload,
      };

    case MAIN_TYPES.EXERCISE_HEIGHTS:
      return {
        ...state,
        exerciseHeights: action.payload,
      };

    case MAIN_TYPES.RESET_STATE:
      return MAIN_INITIAL_STATE;

    case MAIN_TYPES.FIRST_PAGE_LOAD:
      return { ...state, firstPageLoad: action.payload };

    case MAIN_TYPES.OPEN_SNACKBAR_SAVE:
      return { ...state, openSnackbarSave: action.payload };

    case MAIN_TYPES.OPEN_SNACKBAR_SAVED:
      return { ...state, openSnackbarSaved: action.payload };

    case MAIN_TYPES.NAME_CHANGED:
      return { ...state, nameChanged: action.payload };

    case MAIN_TYPES.HAS_UNSAVED_CHANGES:
      return { ...state, hasUnsavedChanges: action.payload };

    case MAIN_TYPES.ADD_OBJECTIVE:
      return {
        ...state,
        objectivesCopy: {
          ...state.objectivesCopy,
          [action.payload.id]: action.payload,
        },
      };

    case MAIN_TYPES.REMOVE_OBJECTIVE:
      const updatedObjectives = { ...state.objectivesCopy };
      delete updatedObjectives[action.payload];

      return {
        ...state,
        objectivesCopy: updatedObjectives,
      };

    case MAIN_TYPES.OVERWRITE_OBJECTIVES:
      return {
        ...state,
        objectivesCopy: action.payload,
      };

    case MAIN_TYPES.ZOOM_FACTOR:
      return {
        ...state,
        zoomFactor: action.payload,
      };

    case  MAIN_TYPES.SCROLL_HEIGHT:
      return {
        ...state,
        scrollHeight: action.payload,
      }

    default:
      return state;
  }
};
